import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { X, ArrowCounterClockwise, FloppyDisk, Palette, SlidersHorizontal, Cards, Cursor } from "@phosphor-icons/react";

// Mirrors DEFAULT_WIDGET_SETTINGS in backend/src/appActions/printful/services.js.
export const DEFAULTS = {
  theme: "light",
  accent: "#CA8A04",
  ink: "#1C1917",
  surface: "#FFFFFF",
  background: "#FAFAF9",
  text: "#0C0A09",
  radius: 14,
  font: "Nunito Sans",
  columns: 4,
  card_style: "border",
  image_fit: "contain",
  hover_zoom: true,

  title: "",
  page_size: 24,
  default_view: "grid",
  default_sort: "name",
  show_search: true,
  show_sort: true,
  show_view_toggle: true,
  show_refresh: true,
  show_count: true,
  show_pagination: true,
  show_chips: true,
  show_product_name: true,
  search_placeholder: "Search products…",
  empty_text: "No products in this store yet.",

  detail_mode: "modal",
  show_detail_kpis: true,
  show_variants: true,
  show_variant_prices: true,
  show_print_files: false,
  show_product_info: true,
  show_stock: true,

  cta_enabled: false,
  cta_label: "Buy now",
  cta_url: "",
  cta_new_tab: true,
};

const SECTIONS = [
  { key: "appearance", label: "Appearance", icon: <Palette size={15} weight="bold" /> },
  { key: "layout", label: "Layout & modules", icon: <Cards size={15} weight="bold" /> },
  { key: "detail", label: "Product detail", icon: <SlidersHorizontal size={15} weight="bold" /> },
  { key: "cta", label: "Call to action", icon: <Cursor size={15} weight="bold" /> },
];

const Customize = ({ initial, saving, onSave, onClose, onPreview }) => {
  const [form, setForm] = useState({ ...DEFAULTS, ...(initial || {}) });
  const [section, setSection] = useState("appearance");

  useEffect(() => {
    setForm({ ...DEFAULTS, ...(initial || {}) });
  }, [initial]);

  // Live-preview every change in the panel behind it.
  const set = (key, value) => {
    const next = { ...form, [key]: value };
    setForm(next);
    onPreview && onPreview(next);
  };

  const Field = ({ label, hint, children }) => (
    <div className="mb-3">
      <label className="d-block fw-semibold mb-1" style={{ fontSize: ".82rem" }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: ".74rem", color: "#78716C", marginTop: 4 }}>{hint}</div>}
    </div>
  );

  const Toggle = ({ k, label }) => (
    <div className="form-check form-switch d-flex align-items-center gap-2 mb-2 ps-0">
      <input
        className="form-check-input m-0"
        type="checkbox"
        role="switch"
        id={`cz-${k}`}
        checked={!!form[k]}
        onChange={(e) => set(k, e.target.checked)}
        style={{ cursor: "pointer" }}
      />
      <label className="form-check-label" htmlFor={`cz-${k}`} style={{ fontSize: ".85rem", cursor: "pointer" }}>
        {label}
      </label>
    </div>
  );

  const Color = ({ k, label }) => (
    <div className="d-flex align-items-center justify-content-between mb-2">
      <span style={{ fontSize: ".85rem" }}>{label}</span>
      <span className="d-flex align-items-center gap-2">
        <input
          type="text"
          value={form[k]}
          onChange={(e) => set(k, e.target.value)}
          className="form-control form-control-sm"
          style={{ width: 96, fontSize: ".78rem", fontFamily: "monospace" }}
          aria-label={`${label} hex`}
        />
        <input
          type="color"
          value={form[k]}
          onChange={(e) => set(k, e.target.value)}
          className="form-control form-control-color p-0 border-0"
          style={{ width: 34, height: 30, cursor: "pointer" }}
          aria-label={`${label} picker`}
        />
      </span>
    </div>
  );

  const Select = ({ k, options }) => (
    <select
      className="form-select form-select-sm"
      value={form[k]}
      onChange={(e) => set(k, e.target.value)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );

  const Num = ({ k, min, max }) => (
    <input
      type="number"
      className="form-control form-control-sm"
      min={min}
      max={max}
      value={form[k]}
      onChange={(e) => set(k, Number(e.target.value))}
    />
  );

  const Text = ({ k, placeholder }) => (
    <input
      type="text"
      className="form-control form-control-sm"
      value={form[k]}
      placeholder={placeholder}
      onChange={(e) => set(k, e.target.value)}
    />
  );

  return (
    <>
      <div className="cz-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="cz" role="dialog" aria-modal="true" aria-label="Customize store widget">
        <style>{`
          .cz-backdrop { position: fixed; inset: 0; background: rgba(12,10,9,.5); z-index: 1090; }
          .cz {
            position: fixed; top: 0; right: 0; bottom: 0; width: min(460px, 100%);
            background: #fff; z-index: 1095; display: flex; flex-direction: column;
            box-shadow: -18px 0 46px rgba(12,10,9,.24); font-family: 'Nunito Sans', system-ui, sans-serif;
          }
          .cz-head { background: linear-gradient(135deg,#1C1917,#292524); color: #fff; padding: 16px 18px; }
          .cz-nav { display: flex; gap: 4px; padding: 10px 12px 0; border-bottom: 1px solid #E7E5E4; flex-wrap: wrap; }
          .cz-nav button {
            border: 0; background: none; font: inherit; font-size: .8rem; font-weight: 600; color: #57534E;
            padding: 8px 10px; border-bottom: 2px solid transparent; cursor: pointer;
            display: inline-flex; align-items: center; gap: 6px;
          }
          .cz-nav button.active { color: #0C0A09; border-bottom-color: #CA8A04; }
          .cz-body { overflow-y: auto; padding: 16px 18px; flex: 1; }
          .cz-foot { border-top: 1px solid #E7E5E4; padding: 12px 18px; display: flex; gap: 8px; align-items: center; }
          .cz-group { border: 1px solid #E7E5E4; border-radius: .75rem; padding: 12px; margin-bottom: 14px; }
          .cz-group h6 { font-size: .78rem; text-transform: uppercase; letter-spacing: .04em; color: #78716C; margin: 0 0 10px; }
        `}</style>

        <div className="cz-head d-flex align-items-center gap-2">
          <div className="flex-grow-1">
            <div className="fw-bold" style={{ fontSize: "1rem" }}>Customize store</div>
            <div style={{ fontSize: ".78rem", opacity: .8 }}>Applies to the embedded widget everywhere</div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-light rounded-circle d-flex align-items-center justify-content-center"
            style={{ width: 34, height: 34 }}
            onClick={onClose}
            aria-label="Close customizer"
          >
            <X size={15} weight="bold" />
          </button>
        </div>

        <div className="cz-nav">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              className={section === s.key ? "active" : ""}
              onClick={() => setSection(s.key)}
            >
              {s.icon} {s.label}
            </button>
          ))}
        </div>

        <div className="cz-body">
          {section === "appearance" && (
            <>
              <div className="cz-group">
                <h6>Theme</h6>
                <Field label="Mode">
                  <Select k="theme" options={[{ value: "light", label: "Light" }, { value: "dark", label: "Dark" }]} />
                </Field>
                <Color k="accent" label="Accent" />
                <Color k="ink" label="Buttons / headers" />
                <Color k="surface" label="Card background" />
                <Color k="background" label="Page background" />
                <Color k="text" label="Text" />
              </div>

              <div className="cz-group">
                <h6>Style</h6>
                <Field label="Font">
                  <Select
                    k="font"
                    options={[
                      { value: "Nunito Sans", label: "Nunito Sans" },
                      { value: "Inter", label: "Inter" },
                      { value: "Rubik", label: "Rubik" },
                      { value: "Poppins", label: "Poppins" },
                      { value: "System", label: "System default" },
                    ]}
                  />
                </Field>
                <Field label="Card style">
                  <Select
                    k="card_style"
                    options={[
                      { value: "border", label: "Bordered" },
                      { value: "shadow", label: "Shadow" },
                      { value: "flat", label: "Flat" },
                    ]}
                  />
                </Field>
                <Field label="Corner radius (px)"><Num k="radius" min={0} max={32} /></Field>
                <Field label="Image fit">
                  <Select
                    k="image_fit"
                    options={[{ value: "contain", label: "Contain (whole product)" }, { value: "cover", label: "Cover (fill card)" }]}
                  />
                </Field>
                <Toggle k="hover_zoom" label="Zoom image on hover" />
              </div>
            </>
          )}

          {section === "layout" && (
            <>
              <div className="cz-group">
                <h6>Grid</h6>
                <Field label="Heading above store" hint="Leave empty for no heading">
                  <Text k="title" placeholder="Our products" />
                </Field>
                <Field label="Columns (desktop)"><Num k="columns" min={1} max={6} /></Field>
                <Field label="Products per page"><Num k="page_size" min={4} max={100} /></Field>
                <Field label="Default view">
                  <Select k="default_view" options={[{ value: "grid", label: "Grid" }, { value: "list", label: "List" }]} />
                </Field>
                <Field label="Default sort">
                  <Select
                    k="default_sort"
                    options={[
                      { value: "name", label: "Name A–Z" },
                      { value: "name_desc", label: "Name Z–A" },
                      { value: "variants", label: "Most variants" },
                    ]}
                  />
                </Field>
              </div>

              <div className="cz-group">
                <h6>Show / hide modules</h6>
                <Toggle k="show_search" label="Search box" />
                <Toggle k="show_sort" label="Sort dropdown" />
                <Toggle k="show_view_toggle" label="Grid / list toggle" />
                <Toggle k="show_refresh" label="Refresh button" />
                <Toggle k="show_count" label="Result count" />
                <Toggle k="show_pagination" label="Pagination" />
                <Toggle k="show_product_name" label="Product name on cards" />
                <Toggle k="show_chips" label="Variant / synced chips" />
              </div>

              <div className="cz-group">
                <h6>Wording</h6>
                <Field label="Search placeholder"><Text k="search_placeholder" /></Field>
                <Field label="Empty state text"><Text k="empty_text" /></Field>
              </div>
            </>
          )}

          {section === "detail" && (
            <div className="cz-group">
              <h6>When a product is clicked</h6>
              <Field
                label="Open detail as"
                hint="Page replaces the grid inside the widget, with a back button."
              >
                <Select
                  k="detail_mode"
                  options={[
                    { value: "modal", label: "Popup modal" },
                    { value: "page", label: "Next page (inline)" },
                    { value: "none", label: "Not clickable" },
                  ]}
                />
              </Field>
              <Toggle k="show_detail_kpis" label="Summary KPIs (variants, retail, stock)" />
              <Toggle k="show_variants" label="Variants tab" />
              <Toggle k="show_variant_prices" label="Prices on variants" />
              <Toggle k="show_stock" label="Stock status" />
              <Toggle k="show_print_files" label="Print files tab" />
              <Toggle k="show_product_info" label="Product info tab" />
            </div>
          )}

          {section === "cta" && (
            <div className="cz-group">
              <h6>Buy button</h6>
              <Toggle k="cta_enabled" label="Show a call-to-action button" />
              <Field label="Button label"><Text k="cta_label" placeholder="Buy now" /></Field>
              <Field
                label="Button URL"
                hint="Use {product_id} or {external_id} as placeholders, e.g. https://shop.com/p/{external_id}"
              >
                <Text k="cta_url" placeholder="https://yourshop.com/products/{external_id}" />
              </Field>
              <Toggle k="cta_new_tab" label="Open in a new tab" />
            </div>
          )}
        </div>

        <div className="cz-foot">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary rounded-3 d-flex align-items-center gap-2"
            onClick={() => {
              setForm({ ...DEFAULTS });
              onPreview && onPreview({ ...DEFAULTS });
              toast.info("Reset to defaults — save to apply");
            }}
          >
            <ArrowCounterClockwise size={14} weight="bold" /> Reset
          </button>

          <button
            type="button"
            className="btn btn-sm btn-dark rounded-3 d-flex align-items-center gap-2 ms-auto"
            disabled={saving}
            onClick={() => onSave(form)}
          >
            <FloppyDisk size={14} weight="bold" />
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Customize;
